import React, { memo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Temporarily disabled (uncomment to restore):
// import { ReactComponent as InstagramIcon } from "../../shared/assets/images/instagram.svg";
import { ReactComponent as YoutubeIcon } from "../../shared/assets/images/youtube.svg";

import { IoBugOutline } from "react-icons/io5";

import {
  BugReportModal,
  RequestFootageModal,
} from "../LibraryModals/LibraryModals";

import styles from "./Footer.module.scss";

const NAV_LINKS = [
  { label: "Home",        to: "/"           },
  { label: "About Us",    to: "/aboutUs"    },
  { label: "Contact",     to: "/contactUs"  },
];

// For media companies (moved here from the Library page footer)
const MEDIA_LINKS = [
  { label: "Video Library", to: "/library" },
  { label: "For media buyers", to: "/for-media" },
  { label: "Trust Center", to: "/trust" },
  { label: "API / Feeds", to: "/api" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy",  to: "/privatePolicy" },
  { label: "Terms of Use",    to: "/termsOfUse"    },
];

const SOCIAL = [
  // Instagram temporarily disabled (uncomment to restore):
  // {
  //   href:  "https://www.instagram.com/vbear_media",
  //   label: "Instagram",
  //   icon:  <InstagramIcon />,
  // },
  {
    href:  "https://www.youtube.com/@vbear_media",
    label: "YouTube",
    icon:  <YoutubeIcon width="18px" />,
  },
];

const Footer = memo(() => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const year = new Date().getFullYear();
  const [requestOpen, setRequestOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const hideCta =
    // home and about have their own creators block
    pathname === "/" ||
    pathname === "/aboutUs" ||
    pathname === "/ugc-production" ||
    pathname.startsWith("/submitVideo") ||
    pathname.startsWith("/submitVideos") ||
    pathname === "/questionnaire" ||
    pathname === "/completedAgreement" ||
    pathname.startsWith("/researcher") ||
    pathname.startsWith("/release/") ||
    // invoices are documents
    /^\/library\/(account|clients)\/invoices\//.test(pathname);

  return (
    <footer className={styles.footer}>
      {/* ── CTA band ── */}
      {!hideCta && <div className={styles.ctaBand}>
        <div className={styles.ctaInner}>
          <div>
            <p className={styles.ctaEyebrow}>Got a viral clip?</p>
            <h2 className={styles.ctaHeadline}>
              Submit your&nbsp;<em>video</em>&nbsp;today
            </h2>
          </div>
          <Link to="/submitVideo" className={styles.ctaBtn}>
            Submit video
          </Link>
        </div>
      </div>}

      {/* ── Main body ── */}
      <div className={styles.body}>
        <div className={styles.bodyInner}>

          {/* Brand column */}
          <div className={styles.brandCol}>
            <Link to="/" className={styles.brandLogo}>
              <img
                src="/assets/images/VBear_name.png"
                alt="VBear"
                className={styles.brandLogoImg}
                width="122"
                height="32"

                loading="lazy"
                decoding="async"
              />

            </Link>
            <p className={styles.brandTagline}>
              We license &amp; monetise viral content<br />
              for TV, digital &amp; social worldwide.
            </p>
            <a
              href="mailto:licensing@vbear.media"
              className={styles.brandEmail}
            >
              licensing@vbear.media
            </a>
          </div>

          {/* Nav column */}
          <div className={styles.navCol}>
            <p className={styles.colTitle}>Company</p>
            <nav>
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className={styles.navLink}>
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Media column */}
          <div className={styles.navCol}>
            <p className={styles.colTitle}>For media</p>
            <nav>
              {MEDIA_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className={styles.navLink}>
                  {l.label}
                </Link>
              ))}
              <button
                type="button"
                className={styles.navLink}
                onClick={() => setRequestOpen(true)}
              >
                Sourcing desk
              </button>
            </nav>
          </div>

          {/* Social column */}
          <div className={styles.socialCol}>
            <p className={styles.colTitle}>Follow us</p>
            <div className={styles.socialRow}>
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  className={styles.socialBtn}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className={styles.bottomBar}>
        <div className={styles.bottomInner}>
          <span className={styles.copyright}>
            © {year} VBear Media. All rights reserved.
          </span>
          <div className={styles.legalLinks}>
            {LEGAL_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={styles.legalLink}>
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              className={styles.legalLink}
              onClick={() => setBugOpen(true)}
            >
              <IoBugOutline />
              Report a bug
            </button>
          </div>
        </div>
      </div>
      {requestOpen && (
        <RequestFootageModal onClose={() => setRequestOpen(false)} />
      )}
      {bugOpen && <BugReportModal onClose={() => setBugOpen(false)} />}
    </footer>
  );
});

export default Footer;
